import { useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useCreateManagementFee,
  useDeleteManagementFee,
  useManagementFees,
  useUpdateManagementFee,
} from '@/hooks/react-query/useManagementFees';
import type { Fund } from '@/types';
import {
  MANAGEMENT_FEE_ACCRUAL_FREQUENCY_LABELS,
  MANAGEMENT_FEE_CALCULATION_BASIS_LABELS,
  MANAGEMENT_FEE_FUND_PERIOD_LABELS,
  ManagementFeeAccrualFrequency,
  ManagementFeeCalculationBasis,
  ManagementFeeFundPeriod,
  type ManagementFeeSchedule,
} from '@/types/management-fees';
import { buildMutationErrorHandler } from '@/utils/form-errors';
import { addYearsToDate } from '@/utils/date-operations';
import { formatDate, formatPercentage } from '@/utils/formatters';
import { Pencil, Plus, Trash2 } from 'lucide-react';

const DURATION_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function defaultsForPeriod(
  period: ManagementFeeFundPeriod,
  fund?: Fund
): {
  effective_from: string;
  calculation_basis: ManagementFeeCalculationBasis;
  description: string;
} {
  if (period === ManagementFeeFundPeriod.INVESTMENT_PERIOD) {
    return {
      effective_from:
        fund?.first_closing_date ?? new Date().toISOString().slice(0, 10),
      calculation_basis: ManagementFeeCalculationBasis.COMMITTED_CAPITAL,
      description: MANAGEMENT_FEE_FUND_PERIOD_LABELS[period],
    };
  }
  const investmentEnd =
    fund?.first_closing_date && fund?.investment_period_years
      ? addYearsToDate(fund.first_closing_date, fund.investment_period_years)
      : new Date().toISOString().slice(0, 10);
  return {
    effective_from: investmentEnd,
    calculation_basis: ManagementFeeCalculationBasis.INVESTED_CAPITAL,
    description: MANAGEMENT_FEE_FUND_PERIOD_LABELS[period],
  };
}

const EMPTY_FORM = {
  fund_period: ManagementFeeFundPeriod.INVESTMENT_PERIOD,
  effective_from: new Date().toISOString().slice(0, 10),
  effective_to: '',
  duration: '',
  description: '',
  calculation_basis: ManagementFeeCalculationBasis.COMMITTED_CAPITAL,
  annual_rate: '2.00',
  accrual_frequency: ManagementFeeAccrualFrequency.QUARTERLY,
};

type DateMode = 'duration' | 'date';

interface ManagementFeesSectionProps {
  fundId: string;
  fund?: Fund;
}

interface FeeSchedulesTableProps {
  isLoading: boolean;
  schedules: ManagementFeeSchedule[];
  onEdit: (schedule: ManagementFeeSchedule) => void;
  onDelete: (schedule: ManagementFeeSchedule) => void;
}

function FeeSchedulesTable({
  isLoading,
  schedules,
  onEdit,
  onDelete,
}: FeeSchedulesTableProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Loading management fees...
      </div>
    );
  }
  if (schedules.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No fee schedules yet. Click &quot;Add Schedule&quot; to create one.
      </div>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Fund Period</TableHead>
          <TableHead>Effective Period</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Calculation Basis</TableHead>
          <TableHead className="text-right">Rate (Annual)</TableHead>
          <TableHead>Accrual Frequency</TableHead>
          <TableHead className="w-[88px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {schedules.map((schedule) => (
          <TableRow key={schedule.id}>
            <TableCell>
              <Badge variant="outline">
                {MANAGEMENT_FEE_FUND_PERIOD_LABELS[schedule.fund_period]}
              </Badge>
            </TableCell>
            <TableCell>
              {formatDate(schedule.effective_from)}
              {' - '}
              {schedule.effective_to
                ? formatDate(schedule.effective_to)
                : 'Indefinite'}
            </TableCell>
            <TableCell className="font-medium">
              {schedule.description || '-'}
            </TableCell>
            <TableCell>
              <Badge variant="outline">
                {
                  MANAGEMENT_FEE_CALCULATION_BASIS_LABELS[
                    schedule.calculation_basis
                  ]
                }
              </Badge>
            </TableCell>
            <TableCell className="text-right">
              {formatPercentage(schedule.annual_rate)}
            </TableCell>
            <TableCell>
              <Badge variant="secondary">
                {
                  MANAGEMENT_FEE_ACCRUAL_FREQUENCY_LABELS[
                    schedule.accrual_frequency
                  ]
                }
              </Badge>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onEdit(schedule)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive"
                  onClick={() => onDelete(schedule)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function FundFeesManagementFees({
  fundId,
  fund,
}: ManagementFeesSectionProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data: schedulesPage, isLoading } = useManagementFees(fundId, {
    page,
    page_size: pageSize,
  });
  const createMutation = useCreateManagementFee(fundId);
  const updateMutation = useUpdateManagementFee(fundId);
  const deleteMutation = useDeleteManagementFee(fundId);

  const [showDialog, setShowDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] =
    useState<ManagementFeeSchedule | null>(null);
  const [deletingSchedule, setDeletingSchedule] =
    useState<ManagementFeeSchedule | null>(null);
  const [feeForm, setFeeForm] = useState(EMPTY_FORM);
  const [dateMode, setDateMode] = useState<DateMode>('duration');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const schedules = schedulesPage?.items ?? [];
  const totalCount = schedulesPage?.total_count ?? 0;

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  const openAddDialog = () => {
    setEditingSchedule(null);
    const defaults = defaultsForPeriod(
      ManagementFeeFundPeriod.INVESTMENT_PERIOD,
      fund
    );
    setFeeForm({
      ...EMPTY_FORM,
      effective_from: defaults.effective_from,
      calculation_basis: defaults.calculation_basis,
      description: defaults.description,
    });
    setDateMode('duration');
    setFormErrors({});
    setShowDialog(true);
  };

  const openEditDialog = (schedule: ManagementFeeSchedule) => {
    setEditingSchedule(schedule);
    setFeeForm({
      fund_period: schedule.fund_period,
      effective_from: schedule.effective_from,
      effective_to: schedule.effective_to ?? '',
      duration: '',
      description: schedule.description ?? '',
      calculation_basis: schedule.calculation_basis,
      annual_rate: String(Number((schedule.annual_rate * 100).toFixed(4))),
      accrual_frequency: schedule.accrual_frequency,
    });
    setDateMode('date');
    setFormErrors({});
    setShowDialog(true);
  };

  const closeDialog = () => {
    setEditingSchedule(null);
    setFeeForm(EMPTY_FORM);
    setDateMode('duration');
    setShowDialog(false);
  };

  const handleSubmitSchedule = () => {
    const errors: Record<string, string> = {};
    if (!feeForm.fund_period) errors.fund_period = 'Fund period is required';
    if (!feeForm.effective_from)
      errors.effective_from = 'Effective from date is required';
    if (
      feeForm.effective_from &&
      feeForm.effective_to &&
      feeForm.effective_to <= feeForm.effective_from
    )
      errors.effective_to = 'Effective to must be after effective from';
    if (!feeForm.calculation_basis)
      errors.calculation_basis = 'Calculation basis is required';
    if (!feeForm.accrual_frequency)
      errors.accrual_frequency = 'Accrual frequency is required';
    if (!feeForm.annual_rate) {
      errors.annual_rate = 'Annual rate is required';
    } else {
      const parsedRate = Number(feeForm.annual_rate);
      if (Number.isNaN(parsedRate) || parsedRate < 0 || parsedRate > 100) {
        errors.annual_rate = 'Annual rate must be between 0 and 100';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const normalizedDescription = feeForm.description.trim();
    const computedEffectiveTo =
      dateMode === 'duration' && feeForm.duration && feeForm.effective_from
        ? addYearsToDate(feeForm.effective_from, Number(feeForm.duration))
        : feeForm.effective_to || null;
    const payload = {
      fund_period: feeForm.fund_period as ManagementFeeFundPeriod,
      effective_from: feeForm.effective_from,
      effective_to: computedEffectiveTo,
      description: normalizedDescription || null,
      calculation_basis:
        feeForm.calculation_basis as ManagementFeeCalculationBasis,
      annual_rate: Number((Number(feeForm.annual_rate) / 100).toFixed(4)),
      accrual_frequency:
        feeForm.accrual_frequency as ManagementFeeAccrualFrequency,
    };

    const onError = buildMutationErrorHandler(setFormErrors, {
      // This dialog has no general-error slot; non-field errors rely on
      // the mutation's own toast instead.
      onFallbackError: () => {},
    });

    if (editingSchedule) {
      updateMutation.mutate(
        {
          scheduleId: editingSchedule.id,
          data: payload,
        },
        { onSuccess: closeDialog, onError }
      );
      return;
    }

    createMutation.mutate(payload, { onSuccess: closeDialog, onError });
  };

  const handleDeleteConfirm = () => {
    if (!deletingSchedule) return;
    deleteMutation.mutate(deletingSchedule.id, {
      onSuccess: () => setDeletingSchedule(null),
    });
  };

  const isMutating =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Fee Schedules</CardTitle>
          <Button size="sm" onClick={openAddDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Add Schedule
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <FeeSchedulesTable
          isLoading={isLoading}
          schedules={schedules}
          onEdit={openEditDialog}
          onDelete={setDeletingSchedule}
        />

        {totalCount > 0 && (
          <Pagination
            current={page}
            total={totalCount}
            pageSize={pageSize}
            onChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </CardContent>

      <Dialog open={showDialog} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingSchedule ? 'Edit Fee Schedule' : 'Add Fee Schedule'}
            </DialogTitle>
            <DialogDescription>
              Configure the rules for calculating management fees.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div className="space-y-2">
              <Label htmlFor="management-fee-period" className="text-base">
                Fund Period
              </Label>
              <Select
                value={feeForm.fund_period}
                onValueChange={(value) => {
                  const period = value as ManagementFeeFundPeriod;
                  if (!editingSchedule) {
                    const defaults = defaultsForPeriod(period, fund);
                    setFeeForm({
                      ...feeForm,
                      fund_period: period,
                      effective_from: defaults.effective_from,
                      effective_to: '',
                      duration: '',
                      calculation_basis: defaults.calculation_basis,
                      description: defaults.description,
                    });
                  } else {
                    setFeeForm({ ...feeForm, fund_period: period });
                  }
                }}
              >
                <SelectTrigger id="management-fee-period">
                  <SelectValue placeholder="Select fund period" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(ManagementFeeFundPeriod).map((period) => (
                    <SelectItem key={period} value={period}>
                      {MANAGEMENT_FEE_FUND_PERIOD_LABELS[period]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formErrors.fund_period && (
                <p className="text-sm text-destructive">
                  {formErrors.fund_period}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label
                  htmlFor="management-fee-effective-from"
                  className="text-base"
                >
                  Effective From
                </Label>
                <Input
                  id="management-fee-effective-from"
                  type="date"
                  value={feeForm.effective_from}
                  onChange={(e) =>
                    setFeeForm({ ...feeForm, effective_from: e.target.value })
                  }
                />
                {formErrors.effective_from && (
                  <p className="text-sm text-destructive">
                    {formErrors.effective_from}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                {dateMode === 'duration' ? (
                  <>
                    <Label
                      htmlFor="management-fee-duration"
                      className="text-base"
                    >
                      Duration
                    </Label>
                    <Select
                      value={feeForm.duration}
                      onValueChange={(value) =>
                        setFeeForm({
                          ...feeForm,
                          duration: value,
                          effective_to: '',
                        })
                      }
                    >
                      <SelectTrigger id="management-fee-duration">
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        {DURATION_OPTIONS.map((years) => (
                          <SelectItem key={years} value={String(years)}>
                            {years} {years === 1 ? 'year' : 'years'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      className="text-xs text-muted-foreground underline hover:text-foreground"
                      onClick={() => {
                        const computedTo =
                          feeForm.duration && feeForm.effective_from
                            ? addYearsToDate(
                                feeForm.effective_from,
                                Number(feeForm.duration)
                              )
                            : '';
                        setDateMode('date');
                        setFeeForm({
                          ...feeForm,
                          effective_to: computedTo,
                          duration: '',
                        });
                      }}
                    >
                      Set exact end date
                    </button>
                  </>
                ) : (
                  <>
                    <Label
                      htmlFor="management-fee-effective-to"
                      className="text-base"
                    >
                      Effective To
                    </Label>
                    <Input
                      id="management-fee-effective-to"
                      type="date"
                      value={feeForm.effective_to}
                      onChange={(e) =>
                        setFeeForm({
                          ...feeForm,
                          effective_to: e.target.value,
                          duration: '',
                        })
                      }
                    />
                    {formErrors.effective_to && (
                      <p className="text-sm text-destructive">
                        {formErrors.effective_to}
                      </p>
                    )}
                    <button
                      type="button"
                      className="text-xs text-muted-foreground underline hover:text-foreground"
                      onClick={() => {
                        setDateMode('duration');
                        setFeeForm({
                          ...feeForm,
                          effective_to: '',
                          duration: '',
                        });
                      }}
                    >
                      Set duration instead
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="management-fee-basis" className="text-base">
                Calculation Basis
              </Label>
              <Select
                value={feeForm.calculation_basis}
                onValueChange={(value) =>
                  setFeeForm({
                    ...feeForm,
                    calculation_basis: value as ManagementFeeCalculationBasis,
                  })
                }
              >
                <SelectTrigger id="management-fee-basis">
                  <SelectValue placeholder="Select basis" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(ManagementFeeCalculationBasis).map((basis) => (
                    <SelectItem key={basis} value={basis}>
                      {MANAGEMENT_FEE_CALCULATION_BASIS_LABELS[basis]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formErrors.calculation_basis && (
                <p className="text-sm text-destructive">
                  {formErrors.calculation_basis}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="management-fee-rate" className="text-base">
                  Rate (% per annum)
                </Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="management-fee-rate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="2.00"
                    value={feeForm.annual_rate}
                    onChange={(e) =>
                      setFeeForm({ ...feeForm, annual_rate: e.target.value })
                    }
                  />
                  <span className="text-muted-foreground font-semibold">%</span>
                </div>
                {formErrors.annual_rate && (
                  <p className="text-sm text-destructive">
                    {formErrors.annual_rate}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="management-fee-frequency" className="text-base">
                  Accrual Frequency
                </Label>
                <Select
                  value={feeForm.accrual_frequency}
                  onValueChange={(value) =>
                    setFeeForm({
                      ...feeForm,
                      accrual_frequency: value as ManagementFeeAccrualFrequency,
                    })
                  }
                >
                  <SelectTrigger id="management-fee-frequency">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(ManagementFeeAccrualFrequency).map(
                      (frequency) => (
                        <SelectItem key={frequency} value={frequency}>
                          {MANAGEMENT_FEE_ACCRUAL_FREQUENCY_LABELS[frequency]}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                {formErrors.accrual_frequency && (
                  <p className="text-sm text-destructive">
                    {formErrors.accrual_frequency}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="management-fee-description" className="text-base">
                Description
              </Label>
              <Input
                id="management-fee-description"
                placeholder="Investment Period"
                value={feeForm.description}
                onChange={(e) =>
                  setFeeForm({ ...feeForm, description: e.target.value })
                }
              />
              {formErrors.description && (
                <p className="text-sm text-destructive">
                  {formErrors.description}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleSubmitSchedule} disabled={isMutating}>
              {editingSchedule ? 'Save Changes' : 'Add Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deletingSchedule}
        onOpenChange={(open) => !open && setDeletingSchedule(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Management Fee Schedule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;
              {deletingSchedule?.description}
              &quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
